// @flow
/* eslint-disable ta-code-conventions/has-matching-test,ta-code-conventions/has-matching-storydoc */

import * as React from 'react';
import { connect } from 'react-redux';
import { Opener } from '@ta/overlays.managers';

import { Track } from '@ta/common.tracking';

import localStorage from '@ta/common.localstorage';
import WindowScroll from '@ta/events.window-scroll';
import { featureIsEnabled } from '@ta/common.features';
import sessionStorage from '@ta/common.sessionstorage';

import { getState } from '@ta/social.tooltip-state';
import ImprovedScrollFeedToolTip from './ImprovedScrollFeedToolTip';

/**
 *
 * This component prompts the user to scroll down on the feed on scoped home. TRVX-11324
 *
 * */

const SCROLL_OFFSET = 54;
const MAX_TA_SOCIAL_SCROLL_KEY = 'maxTASocialScroll';
const THROTTLE_WAIT = 600;
const NUM_PIXEL_OFFSET_FROM_TOP_OF_FEED = 150; // How many pixels offset from top of feed to be considered scrolled

/** Local storage key to use to force show scroll prompt on scoped home only */
const FORCE_SCROLL_PROMPT = 'show_scoped_home_scroll_prompt';

type ExternalProps = {|
  /* Which element to scroll to when you click the button */
  elementToScrollTo?: React.ElementRef<*>,
  feedImpressionId: ?string,
|};

type Props = {|
  puid: string,
  shouldForceShowViaRedux: boolean,
  ...ExternalProps,
|};

type State = {|
  isDelayedAfterMount: boolean,
  clickedTooltip: boolean,
  hasScrolled: boolean,
  shouldForceShowViaSessionStorage: boolean,
|};

class HomeFeedScrollPrompt extends React.Component<Props, State> {
  static defaultProps = {
    elementToScrollTo: undefined,
  };

  state = {
    isDelayedAfterMount: false,
    clickedTooltip: false,
    hasScrolled: false,
    shouldForceShowViaSessionStorage: false,
  };

  componentDidMount() {
    this.timeoutId = setTimeout(() => {
      this.setState({ isDelayedAfterMount: true });
    }, 500);

    if (sessionStorage.has(FORCE_SCROLL_PROMPT)) {
      this.setState({ shouldForceShowViaSessionStorage: true });
      sessionStorage.remove(FORCE_SCROLL_PROMPT);
    }
  }

  componentWillUnmount() {
    // Avoid console errors if setTimeout tries to set state after dismount.
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  onClick = () => {
    if (this.props.elementToScrollTo) {
      // eslint-disable-next-line no-restricted-globals
      window.scrollTo({
        top:
          this.props.elementToScrollTo.current.getBoundingClientRect().top +
          // eslint-disable-next-line no-restricted-globals
          window.pageYOffset -
          SCROLL_OFFSET,
        behavior: 'smooth',
      });
    }

    this.setState({ clickedTooltip: true });
  };

  onScroll = () => {
    if (!this.state.hasScrolled) {
      this.setState({ hasScrolled: true });
    }
  };

  isGhost = () => {
    if (this.props.shouldForceShowViaRedux || this.state.shouldForceShowViaSessionStorage) {
      return false;
    }

    return !featureIsEnabled('social_scoped_home_scrolling_tooltip');
  };

  hasNotScrolled = () =>
    // eslint-disable-next-line no-restricted-globals
    !this.state.hasScrolled && window.pageYOffset < NUM_PIXEL_OFFSET_FROM_TOP_OF_FEED;

  shouldShow = (isOnboardingModalOpen: boolean) => {
    const maxScrolled = localStorage.get(MAX_TA_SOCIAL_SCROLL_KEY);

    return (
      !isOnboardingModalOpen &&
      this.state.isDelayedAfterMount &&
      this.hasNotScrolled() &&
      !this.state.clickedTooltip &&
      (!maxScrolled ||
        maxScrolled < 3 ||
        this.props.shouldForceShowViaRedux ||
        this.state.shouldForceShowViaSessionStorage)
    );
  };

  timeoutId: ?TimeoutID;

  render() {
    return (
      <Opener name="onboarding-interstitial">
        {(open, isOnboardingModalOpen) => {
          if (this.shouldShow(isOnboardingModalOpen)) {
            // The tracking uses the fields for non standard purposes, see: https://docs.google.com/spreadsheets/d/1Pj_6dNf3U65VVAbSvImOdk39h1cplKkyb-rPNhATSEk/edit#gid=0
            return (
              <Track
                mount={
                  this.isGhost()
                    ? {
                        module: 'social_tooltip | discover_your_feed_ghost',
                        action: 'impression',
                        context: this.props.feedImpressionId ? this.props.feedImpressionId : '',
                        parentUid: this.props.puid,
                      }
                    : {
                        module: 'social_tooltip | discover_your_feed',
                        action: 'impression',
                        context: this.props.feedImpressionId ? this.props.feedImpressionId : '',
                        parentUid: this.props.puid,
                      }
                }
                click={{
                  module: 'social_tooltip | discover_your_feed',
                  action: 'click',
                  context: this.props.feedImpressionId ? this.props.feedImpressionId : '',
                  parentUid: this.props.puid,
                }}
              >
                <ImprovedScrollFeedToolTip onClick={this.onClick} isGhost={this.isGhost()} />
                <WindowScroll callback={this.onScroll} throttle={THROTTLE_WAIT} />
              </Track>
            );
          }
          return null;
        }}
      </Opener>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  puid: state.tracking.uid,
  shouldForceShowViaRedux: getState(state).forceShowScrollTooltip,
}))(HomeFeedScrollPrompt);
